import React, { useState, useEffect, useRef } from 'react';
import { addToCart } from "frontend/js/useShopifyCart";
import ImageComponent from "@project-stories/elements/Image/Image";
import shopify from "frontend/js/shopify";

const ProductSuitKit = ({ settings }) => {
  const metafields = settings.metafields || {};
  const referencedProducts = [
    { key: "matching_jacket", label: "Jacket", product: metafields?.matching_jacket },
    { key: "matching_vest", label: "Waistcoat", product: metafields?.matching_vest },
    { key: "matching_trouser", label: "Trousers", product: metafields?.matching_trouser }
  ].filter(p => p.product && p.product.featured_image);

  // Early return if no products
  if (referencedProducts.length === 0) {
    return (
      <div className="pb-4 suit-kit-size-selector lg:pb-0">
        <div className="text-gray-500">No suit kit products available</div>
      </div>
    );
  }

  const [selectedItems, setSelectedItems] = useState(() => {
    const initial = {};
    referencedProducts.forEach(ref => {
      initial[ref.label] = true;
    });
    return initial;
  });

  const [selectedSizes, setSelectedSizes] = useState(() => {
    const initial = {};
    referencedProducts.forEach(ref => {
      initial[ref.label] = ref.product.variants?.[0]?.id || "";
    });
    return initial;
  });

  const [openDropdown, setOpenDropdown] = useState<{[key: string]: boolean}>({});
  const dropdownRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  const [combinedStoreInventory, setCombinedStoreInventory] = useState<{[key: string]: any}>({});
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const toggleDropdown = React.useCallback((label: string) => {
    setOpenDropdown(prev => {
      const isOpen = !!prev[label];
      return { [label]: !isOpen };
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const keys = Object.keys(dropdownRefs.current || {});
      let clickedInside = false;
      for (const key of keys) {
        const node = dropdownRefs.current[key];
        if (node && node.contains(target)) { clickedInside = true; break; }
      }
      if (!clickedInside) setOpenDropdown({});
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleCheckboxChange = React.useCallback((label) => {
    setSelectedItems(prev => ({ ...prev, [label]: !prev[label] }));
  }, []);

  const handleSizeChange = React.useCallback((label, sizeId) => {
    setSelectedSizes(prev => ({ ...prev, [label]: Number(sizeId) }));
  }, []);

  // Check if variant is available based on Combined Store inventory
  const isVariantAvailable = React.useCallback((variant) => {
    if (!variant) return false;
    
    const variantId = String(variant.id);
    
    // If Combined Store inventory data is available, use it
    if (Object.keys(combinedStoreInventory).length > 0) {
      return combinedStoreInventory[variantId]?.available ?? false;
    }
    
    // Fallback to default variant.available if Combined Store data not available
    return variant.available;
  }, [combinedStoreInventory]);

  // Fetch Combined Store inventory on mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchCombinedStoreInventory = async () => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      
      const allVariantIds: (string | number)[] = [];
      referencedProducts.forEach(({ product }) => {
        if (product?.variants) {
          product.variants.forEach(variant => {
            allVariantIds.push(variant.id);
          });
        }
      });

      if (allVariantIds.length > 0 && !isLoadingInventory) {
        setIsLoadingInventory(true);
        
        try {
          const response = await shopify.getInventoryDetails(allVariantIds);
          
          if (signal.aborted || !isMounted) {
            return;
          }
          
          const nodes = response?.data?.nodes || [];
          
          // Check if Combined Store exists in any inventory levels
          const hasCombinedStore = nodes.some(node => {
            const inventoryLevels = node.inventoryItem?.inventoryLevels?.nodes || [];
            return inventoryLevels.some(level => level.location.name === "Combined Store");
          });

          if (hasCombinedStore && isMounted) {
            const inventory = {};
            nodes.forEach(node => {
              const variantId = node.id.split("/").pop();
              const inventoryLevels = node.inventoryItem?.inventoryLevels?.nodes || [];
              const combinedStore = inventoryLevels.find(level => level.location.name === "Combined Store");
              
              inventory[variantId] = {
                available: combinedStore ? combinedStore.quantities[0]?.quantity > 0 : false,
                quantity: combinedStore ? combinedStore.quantities[0]?.quantity || 0 : 0
              };
            });
            setCombinedStoreInventory(inventory);
          }
        } catch (error) {
          if (!signal.aborted && isMounted) {
            console.error('Error fetching Combined Store inventory:', error);
            // Set empty inventory to prevent further attempts
            setCombinedStoreInventory({});
          }
        } finally {
          if (isMounted) {
            setIsLoadingInventory(false);
          }
        }
      }
    };

    fetchCombinedStoreInventory();
    
    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // Only run once on mount

  // Update selected sizes when Combined Store inventory data is available
  useEffect(() => {
    if (Object.keys(combinedStoreInventory).length > 0) {
      setSelectedSizes(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        referencedProducts.forEach(({ label, product }) => {
          const currentSelectedId = String(updated[label]);
          const currentVariant = product.variants?.find(v => String(v.id) === currentSelectedId);
          
          // If current selection is not available in Combined Store, find first available variant
          if (currentVariant && !isVariantAvailable(currentVariant)) {
            const firstAvailable = product.variants?.find(v => isVariantAvailable(v));
            if (firstAvailable && firstAvailable.id !== updated[label]) {
              updated[label] = firstAvailable.id;
              hasChanges = true;
            }
          }
        });
        
        return hasChanges ? updated : prev;
      });
    }
  }, [combinedStoreInventory, isVariantAvailable]);

  // Calculate total price based on selected items and their selected variants
  const calculateTotalPrice = React.useCallback(() => {
    let total = 0;
    referencedProducts.forEach(({ label, product }) => {
      if (selectedItems[label]) {
        const selectedVariantId = selectedSizes[label];
        if (selectedVariantId) {
          const selectedVariant = product.variants?.find(v => String(v.id) === String(selectedVariantId));
          if (selectedVariant) {
            total += selectedVariant.price;
          }
        }
      }
    });
    return total;
  }, [referencedProducts, selectedItems, selectedSizes]);

  const getSelectedVariantPricing = React.useCallback((label, product) => {
    const selectedVariantId = selectedSizes[label];
    const selectedVariant = selectedVariantId
      ? product.variants?.find(v => String(v.id) === String(selectedVariantId))
      : null;

    const currentPrice = selectedVariant?.price ?? product.price;
    const compareAtPrice = selectedVariant?.compare_at_price ?? null;
    const isOnSale = Boolean(compareAtPrice && compareAtPrice > currentPrice);

    return {
      currentPrice,
      compareAtPrice,
      isOnSale
    };
  }, [selectedSizes]);

  const calculateTotalCompareAtPrice = React.useCallback(() => {
    let total = 0;
    let hasCompareAt = false;

    referencedProducts.forEach(({ label, product }) => {
      if (selectedItems[label]) {
        const { currentPrice, compareAtPrice, isOnSale } = getSelectedVariantPricing(label, product);
        total += isOnSale ? compareAtPrice : currentPrice;
        hasCompareAt = hasCompareAt || isOnSale;
      }
    });

    return hasCompareAt ? total : null;
  }, [getSelectedVariantPricing, referencedProducts, selectedItems]);

  const handleSuitKitAdd = React.useCallback(async () => {
    const itemsToAdd: { id: number | string; quantity: number; properties: Object }[] = [];

    referencedProducts.forEach(({ label }) => {
      if (selectedItems[label]) {
        const selectedVariantId = selectedSizes[label];
        if (selectedVariantId) {
          itemsToAdd.push({
            id: selectedVariantId,
            quantity: 1,
            properties: {
              _suitKit: true
            }
          });
        }
      }
    });

    if (itemsToAdd.length > 0) {
      try {
        await addToCart({ items: itemsToAdd });
        console.log('Selected suit kit items added to cart');
      } catch (error) {
        console.error('Error adding items to cart:', error);
      }
    }
  }, [referencedProducts, selectedItems, selectedSizes]);

  useEffect(() => {
    document.addEventListener("add-suit-kit-to-cart", handleSuitKitAdd);
    return () => {
      document.removeEventListener("add-suit-kit-to-cart", handleSuitKitAdd);
    };
  }, [handleSuitKitAdd]);

  const selectedItemsCount = React.useMemo(() => {
    return referencedProducts.filter(({ label }) => selectedItems[label]).length;
  }, [referencedProducts, selectedItems]);

  const totalPrice = calculateTotalPrice();
  const totalCompareAtPrice = calculateTotalCompareAtPrice();

  return (
    <div className="pb-4 suit-kit-size-selector lg:pb-0">
      {isLoadingInventory && (
        <div className="mb-4 text-sm text-gray-500">Loading inventory...</div>
      )}
      <div className="flex flex-col gap-8">
        {referencedProducts.map(({ label, product }, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="flex items-center w-full gap-6 flex-nowrap">
              <a href={product.handle}>
                <ImageComponent
                  imageUrl={product.featured_image}
                  alt={label}
                  classes="w-[75px] object-cover"
                />
              </a>
              <div className="flex flex-col w-full">
                <div className="flex items-center justify-between gap-2 py-2 lg:py-0">
                  <div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="!w-6 !h-6 border-black rounded"
                        checked={selectedItems[label]}
                        onChange={() => handleCheckboxChange(label)}
                      />
                      <span className="ml-2 text-black b2">{label} Size</span>
                    </label>
                  </div>
                  <div className="flex flex-col items-end">
                    {(() => {
                      const { currentPrice, compareAtPrice, isOnSale } = getSelectedVariantPricing(label, product);

                      return (
                        <>
                          <span className={`ml-auto bcp ${isOnSale ? 'price-current--sale' : 'text-[#54565A]'}`}>
                            ${(currentPrice / 100).toFixed(2)}
                          </span>
                          {isOnSale && (
                            <span className="price-compare-at text-[12px] line-through text-[#BBBBBD]">
                              ${(compareAtPrice / 100).toFixed(2)}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div
                  className="relative mt-[15px]"
                  ref={(el) => { dropdownRefs.current[label] = el; }}
                >
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={!!openDropdown[label]}
                    onClick={() => toggleDropdown(label)}
                    className="w-full b2 border-b border-[#00263E] text-[#00263E] py-2 flex items-center justify-between px-0 font-normal appearance-none touch-manipulation"
                  >
                    <span>
                      {product.variants?.find(v => String(v.id) === String(selectedSizes[label]))?.option2 || 'Select size'}
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${openDropdown[label] ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd"/></svg>
                  </button>

                  {openDropdown[label] && (
                    <ul
                      role="listbox"
                      className="absolute left-0 right-0 z-50 px-0 overflow-auto bg-white border border-gray-200 shadow-lg rounded-b-md max-h-64"
                      style={{
                        WebkitAppearance: 'none',
                        WebkitTouchCallout: 'none',
                        WebkitUserSelect: 'none',
                        touchAction: 'manipulation'
                      }}
                    >
                      {product.variants
                        ?.slice()
                        .sort((a, b) => {
                          const sizeA = a.option2;
                          const sizeB = b.option2;
                          const numA = parseFloat(sizeA);
                          const numB = parseFloat(sizeB);
                          if (!isNaN(numA) && !isNaN(numB)) {
                            return numA - numB;
                          }
                          return sizeA.localeCompare(sizeB, undefined, { numeric: true, sensitivity: 'base' });
                        })
                        .map((variant) => {
                          const isDisabled = !isVariantAvailable(variant);
                          const isSelected = String(selectedSizes[label]) === String(variant.id);
                          return (
                            <li
                              key={variant.id}
                              role="option"
                              aria-selected={isSelected}
                              className={`flex items-center justify-between px-4 py-3 cursor-pointer b2 ${isDisabled ? 'text-gray-400 line-through cursor-not-allowed' : 'text-black hover:bg-gray-50'}`}
                              style={{
                                WebkitAppearance: 'none',
                                WebkitTouchCallout: 'none',
                                WebkitUserSelect: 'none',
                                WebkitTapHighlightColor: 'transparent',
                                touchAction: 'manipulation',
                                userSelect: 'none',
                                fontSize: 'inherit',
                                fontFamily: 'inherit',
                                lineHeight: 'inherit',
                                textDecoration: isDisabled ? 'line-through' : 'none',
                                color: isDisabled ? '#9CA3AF' : '#000000',
                                backgroundColor: 'transparent',
                                border: 'none',
                                outline: 'none'
                              }}
                              onTouchStart={(e) => {
                                if (isDisabled) {
                                  e.preventDefault();
                                  return;
                                }
                                e.currentTarget.style.backgroundColor = '#F9FAFB';
                              }}
                              onTouchEnd={(e) => {
                                if (isDisabled) {
                                  e.preventDefault();
                                  return;
                                }
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                              onClick={() => {
                                if (isDisabled) return;
                                handleSizeChange(label, String(variant.id));
                                setOpenDropdown(prev => ({ ...prev, [label]: false }));
                              }}
                            >
                              <span 
                                className={isSelected ? "font-bold" : ""}
                                style={{
                                  WebkitUserSelect: 'none',
                                  userSelect: 'none',
                                  pointerEvents: 'none'
                                }}
                              >
                                {variant.option2}
                              </span>
                              {isSelected && (
                                <svg 
                                  fill="#000000" 
                                  width="16px" 
                                  height="16px" 
                                  viewBox="0 0 1920 1920" 
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    pointerEvents: 'none',
                                    flexShrink: 0
                                  }}
                                >
                                  <path d="M1743.858 267.012 710.747 1300.124 176.005 765.382 0 941.387l710.747 710.871 1209.24-1209.116z" fillRule="evenodd"/>
                                </svg>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Total Price Section */}
      <div className="pt-6 mt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium text-black b1">Total</span>
            <div className="text-[#54565A] text-xs mt-1">
              {selectedItemsCount} item{selectedItemsCount !== 1 ? 's' : ''} selected
            </div>
          </div>
          <div className="flex flex-col items-end">
            <span className={`font-medium bp ${totalCompareAtPrice !== null ? 'price-current--sale' : 'text-black'}`}>
              ${(totalPrice / 100).toFixed(2)}
            </span>
            {totalCompareAtPrice !== null && (
              <span className="price-compare-at text-[12px] line-through text-[#BBBBBD]">
                ${(totalCompareAtPrice / 100).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSuitKit;
